export enum EHttpService {
  amplify = 'amplify',
  partner = 'partner',
  scenarios = 'scenarios',
  agent = 'agent',
  // Agent-service's unauthenticated /guest/agents* routes only — these stay
  // on the old public CoreALB (never dual-mounted behind the gateway's JWT
  // authorizer, by design: an anonymous chat widget has no token to present).
  // Do NOT point this at the shared gateway like `agent` above.
  guestAgent = 'guestAgent',
  text = 'text',
  voip = 'voip',
  internal = 'internal',
  internalV4 = 'internalV4',
}

export enum EMarketingClientPath {
  home = '/',
  examplePage = '/example-page',
  faq = '/faq',
  marketingIntegrations = '/help/integrations',
  marketingPrivacyPolicy = '/help/privacy-policy',
  marketingTermsOfUse = '/help/terms-of-use',
  marketingReferralsTerms = '/help/referrals-terms',
  loadingItem = '/loading/:item',
  payments = '/payments/:paymentStatus',
  product = '/product',
  pricing = '/pricing',
  playground = '/playground',
  facebookDemo = '/facebook-demo',
  referralLink = '/referral/:referralCode',
  referralsAbout = '/referrals-about',
  supportAndContact = '/support-and-contact',
}

export enum EAdminPortalClientPath {
  admin = '/admin',
  orgsAndUsers = `${admin}/orgs-and-users`,
  partnerManagement = `${admin}/partner-management`,
  partnerEdit = `${admin}/partner-management/:partnerId`,
  usageReports = `${admin}/usage-reports`,
  serviceTemplates = `${admin}/admin-templates`,
  phoneNumbers = `${admin}/phone-numbers`,
  user = `${admin}/admin-user/:id`,
  organization = `${admin}/admin-organization/:id`,
  partnerCreate = `${admin}/partner-management/create`,
  promptMetadata = `${admin}/prompt-metadata`,
  campaignTemplates = `${admin}/campaign-templates`,
  campaignTemplatesCreateEdit = `${admin}/campaign-templates/:templateId`,
  campaignTemplateVersionEdit = `${admin}/campaign-templates/:templateId/versions/:versionId`,
  campaignTemplateVersions = `${admin}/campaign-templates/:templateId/versions`,
  planMetadata = `${admin}/plan-metadata`,
  voiceTemplates = `${admin}/voice-templates`,
  portalGeneralSettings = `${admin}/portal-general-settings`,
  portalBranding = `${admin}/portal-branding`,
  manageAdminUsers = `${admin}/manage-admin-users`,
}

export enum EClientPath {
  login = '/login',
  forgotPassword = '/forgot-password',
  termsOfUse = '/terms-of-use',
  overview = '/overview',
  authCallback = '/auth/callback',
  authSignout = '/auth/signout',

  settingsBase = '/settings',
  settingsPostConvoSettings = `${settingsBase}/analysis-settings`,
  settingsAnalysisSettingsV2 = `${settingsBase}/v2/analysis-settings`,
  settingsConvoOptions = `${settingsBase}/agent-preferences`,
  settingsKnowledge = `${settingsBase}/knowledge`,
  // The former Knowledge v3 preview URL; it now redirects to settingsKnowledge (which is v3).
  settingsKnowledgeV3 = `${settingsKnowledge}/v3`,
  settingsIndividualKnowledgeSet = `${settingsKnowledge}/:knowledgeSetId`,
  settingsChatbot = `${settingsBase}/chatbot`,
  settingsEmail = `${settingsBase}/email`,
  settingsPhone = `${settingsBase}/phone`,
  settingsText = `${settingsBase}/text`,
  settingsNotifications = `${settingsBase}/notifications`,
  settingsEscalations = `${settingsBase}/escalations`,
  settingsCoreBehavior = `${settingsBase}/core-behavior`,
  settingsApiKeys = `${settingsBase}/api-keys`,

  inbox = '/inbox',
  inboxMessage = '/inbox/:contactId?',
  // Contact-less chatbot conversation — no Contact row yet (see the
  // Coalesce Contact ticket). Kept as a sibling route rather than
  // overloading :contactId with a discriminator: :contactId is consumed in
  // several places that assume it's a contact UUID, and a distinct path
  // segment avoids making all of them branch on "is this actually a
  // conversationId?".
  inboxConversation = '/inbox/conversation/:conversationId',
  contacts = '/contacts',
  contact = '/contacts/:contactId',
  campaignsBase = '/campaigns',
  campaignList = `${campaignsBase}`,
  campaignSelection = `/new-campaign`,
  campaignCreateEdit = `${campaignsBase}/campaign/:id`,
  campaignOfuManage = `${campaignsBase}/campaign-ofu/:id`,
  campaignOfuCreateEditLegacy = `${campaignsBase}/campaign-ofu-legacy/:id`,
  campaignOfuDetails = `${campaignOfuManage}/details`,
  campaignOfuContacts = `${campaignOfuManage}/contacts`,
  campaignOfuPerformance = `${campaignOfuManage}/performance`,
  campaignOfuAssistants = `${campaignOfuManage}/assistants`,
  campaignOfuConfiguration = `${campaignOfuManage}/edit`,
  campaignOfuDetailsLegacy = `${campaignOfuCreateEditLegacy}/details`,
  campaignOfuContactsLegacy = `${campaignOfuCreateEditLegacy}/contacts`,
  campaignOfuResultsLegacy = `${campaignOfuCreateEditLegacy}/results`,
  campaignDetails = `${campaignCreateEdit}/details`,
  campaignContacts = `${campaignCreateEdit}/contacts`,
  campaignResults = `${campaignCreateEdit}/results`,
  campaignTemplates = `${campaignsBase}/templates`,
  campaignBuilder = `${campaignsBase}/builder/:id`,
  reports = '/reports',
  reportsDashboard = '/reports/:dashboard?',
  privacyPolicy = '/privacy-policy',
  billing = '/billing',
  anything = '*',
  oldChatBase = '/chat',
  oldChat = `${oldChatBase}/:agentId`,
  legacyChat = `${oldChatBase}/legacy/:legacyChatbotId`,
  chatV2Base = `${oldChatBase}/v2`,
  chatV2 = `${chatV2Base}/:agentId`,
  chatBase = '/c',
  chat = `${chatBase}/:friendlyUrl`,

  integrations = '/integrations-help',
  referralsInfo = '/referrals-info',
  referralsTerms = '/referrals-terms',
  learnMore = '/learnmore',
  support = '/support',
  help247 = '/help247',
  plan = '/plan',
  partnerAuth = '/auth',
  account = '/account',
  manageAssistants = '/manage-assistants',

  manageIntegrations = '/manage-integrations',
  manageIntegrationsAbcFitnessPlatform = `${manageIntegrations}/abc-fitness/:platform`,
  manageIntegrationsZapier = `${manageIntegrations}/zapier`,

  manageBrand = '/manage-brand',
  manageBrandCreate = `${manageBrand}/create`,
  manageBrandDetail = `${manageBrand}/:brandId`,

  scenariosBase = '/scenarios',
  manageScenarios = `${scenariosBase}`,
  manageScenariosV2 = `${scenariosBase}/manager`,
  manageScenariosActions = `${scenariosBase}/manager/actions`,
  customScenarioCreate = `${scenariosBase}/custom`,
  customScenarioV2Create = `${scenariosBase}/v2/custom`,
  scenarioCreateEdit = `${scenariosBase}/scenario/:scenarioId`,
  scenarioCreateV2Edit = `${scenariosBase}/v2/scenario/:scenarioId`,

  scenarioActionBase = `${scenariosBase}/actions`,
  scenarioActionCreateEdit = `${scenarioActionBase}/:actionId`,
  scenarioActionV2CreateEdit = `${scenariosBase}/v2/actions/:actionId`,
  users = '/users',

  liveTransfersScenarios = '/live-transfers-scenarios',
  liveTransferCreate = `${liveTransfersScenarios}/new`,
  liveTransferCreateEdit = `${liveTransfersScenarios}/:liveTransferId`,
  voipBase = '/voip',
  voipPhoneNumbers = `${voipBase}/phone-numbers`,
  voipPhoneNumberDetail = `${voipPhoneNumbers}/:numberId`,
  voipDevices = `${voipBase}/devices`,
  voipCalls = `${voipBase}/calls`,
  tcrForm = '/tcr-form',
  home = '/',
  unsubscribe = '/unsubscribe',
  integrationsOauthCallback = '/integrations/oauth/callback',
  quote = `/quotes/:quoteId`,
  notFound = '/404',
}

export enum EServerPath {
  users = '/users',
  apiUsers = '/api/users',
  usersInvite = '/users/invite',
  user = '/api/users/:userId',
  userPermissions = `${user}/permissions`,
  userCognito = '/users/cognito/:cognitoUid',
  orgs = '/orgs',
  agents = '/agents',
  agentsBulk = '/agents/bulk',
  agentsAssignments = '/agents/assignments',
  agentsStatus = '/agents/status',
  agentsTransfers = '/agents/transfers',
  agentsTransfersDisable = '/agents/transfers/disable',
  agentsTransfersEnable = '/agents/transfers/enable',
  webScrapeStatus = '/agents/loadstatus/:agentId',
  agent = '/agents/:agentId',
  guestAgent = '/guest/agents/:agentId',
  guestAgentByFriendlyUrl = '/guest/agents/by-friendly-url/:friendlyUrl',
  guestAgentByLegacyChatbotId = '/guest/agents/by-legacy-id/:legacyChatbotId',
  agentSchedule = '/agents/:agentId/schedule',
  agentPhonePrompt = '/agents/:agentId/prompt',
  agentClone = '/agents/:agentId/clone',
  agentFriendlyUrlAvailable = '/agents/friendly-url-available',
  schedules = '/schedules',
  forms = '/forms',
  form = `${forms}/:formId`,
  schedule = `${schedules}/:scheduleId`,
  scheduleHours = `${schedules}/:scheduleId/hours`,
  scheduleAssign = `${schedules}/:scheduleId/assign`,
  images = '/agents/:agentId/images',
  templates = '/templates',
  template = '/templates/:templateId',
  qualifications = '/qualifications',
  inbox = '/api/crm/contacts/inbox',
  inboxMessages = '/api/crm/contacts/inbox/messages',
  topics = '/api/crm/contacts/inbox/topics',
  contacts = '/api/crm/contacts',
  contact = '/api/crm/contacts/:contactId',
  contactsBatchDelete = '/api/crm/contacts/batchDelete',
  contactsFilterOptions = '/api/crm/contacts/filter-options',
  contactsExport = '/api/crm/contacts/export',
  crmExportJob = '/api/crm/exports/:jobId',
  notes = '/api/crm/notes',
  note = '/api/crm/notes/:id',
  emailAddressAvailability = '/email-addresses/availability',
  emailAddressAssignments = '/email-addresses/assignments',
  conversations = '/api/crm/conversations',
  conversation = '/api/crm/conversations/:conversationId',
  conversationFeed = '/api/crm/conversations/feed',
  reportingConversations = '/api/reporting/conversations',
  adminUsers = '/api/admin/users',
  chatbotSettings = '/agents/chatsettings',
  referralCountForOrg = '/orgs/getReferralCountForOrg/:id',
  updateReferralCode = '/orgs/updateReferralCode/:orgId',
  getRefferalUICustomization = '/orgs/getReferralUICustomization',
  updateReferredByCode = '/orgs/updateReferredByCode/:orgId',
  organization = '/orgs/:id',
  trialDaysLeft = '/orgs/:organizationId/freeTrialDaysLeft',
  checkout = '/stripe/checkout',
  billing = '/stripe/customer-portal',
  partnersByOrgs = '/orgs/partners',
  partners = '/partners',
  partner = '/partners/:id',
  tierChangeV2 = '/api/organizations/:organizationId/tier',
  downgradeToFree = '/api/organizations/:organizationId/downgrade-to-free',
  organizationPlans = '/api/organizations/:organizationId/plans',
  organizationCurrentPlan = `${organizationPlans}/current`,
  organizationAmendPlan = `${organizationPlans}/amend`,
  // Campaign service, through the shared private API (EHttpService.internal).
  campaigns = '/api/campaigns',
  campaignsArchive = `${campaigns}/archive`,
  campaign = `${campaigns}/:id`,
  campaignScheduleSteps = `${campaign}/schedule-steps`,
  campaignClone = `${campaign}/clone`,
  campaignEmailServiceMetrics = `/api/campaign-email-metrics/:campaignId`,
  simulateOfuCampaign = `${campaigns}/simulate`,
  simulateOfuCampaignMessages = `${campaigns}/simulate-messages`,
  renderPrompts = `${campaigns}/render-prompts`,
  evaluatePrompts = `${campaigns}/evaluate-prompts`,
  campaignExecute = `${campaign}/execute`,
  campaignOutreachExecute = `${campaign}/outreaches/execute`,
  campaignContacts = `${campaign}/contacts`,
  campaignContactsTestPopulate = `${campaignContacts}/test-populate`,
  campaignAdvanceTime = `${campaign}/advance-time`,
  campaignContactsEvaluate = `${campaignContacts}/evaluate`,
  campaignContactsBulk = `${campaign}/contacts/bulk`,
  campaignContactsUpload = `${campaign}/contacts/upload`,
  campaignUploadLogs = `${campaign}/upload-logs`,
  campaignUploadLog = `${campaignUploadLogs}/:uploadLogId`,
  campaignContactsPaginated = `${campaign}/contacts/paginated`,
  campaignOutreaches = `${campaign}/outreaches`,
  // Shown to customers as their API endpoint on the external service host
  // (VITE_EXTERNAL_SERVICE): not the portal path, so it never moves with it.
  campaignContactsWebhook = '/campaigns/:id/contacts',
  campaignContact = `${campaign}/contacts/:campaignContactId`,
  campaignContactOutreach = `${campaignContact}/outreaches/:outreachId`,
  campaignConversations = `${campaign}/conversations`,
  campaignContactOutreachExecute = `${campaignContact}/outreaches/:outreachId/execute`,
  campaignExecuteAllApprovedOutreaches = `${campaign}/execute-approved-outreaches`,
  campaignsPhoneReports = `${campaign}/reports/phone`,
  campaignsSummaryReport = `${campaign}/reports/summary`,
  campaignsTextReports = `${campaign}/reports/text`,
  campaignCreateFromTemplate = `${campaigns}/create-from-template`,
  /**
   * Scenario service (EHttpService.scenarios). VITE_SCENARIO_SERVICE is the
   * bare gateway origin (no trailing /api); the gateway only forwards
   * `/api/*`, so the `/api` prefix lives here on the four roots and every
   * child path derives from them.
   */
  scenarios = '/api/scenarios',
  scenario = `${scenarios}/:id`,
  scenarioClone = `${scenario}/clone`,
  scenarioAgents = `${scenarios}/:scenarioId/agents`,
  scenarioAgentsForAdmin = `${scenarioAgents}/all`,
  scenarioAgent = `${scenarioAgents}/:agentId`,
  scenarioTemplates = `${scenarios}/templates`,
  scenarioTemplate = `${scenarioTemplates}/:scenarioTemplateId`,
  scenarioTemplateCreate = `${scenarioTemplate}/create`,
  actions = '/api/actions',
  action = `${actions}/:id`,
  integrations = '/api/integrations',
  integrationsCatalog = `${integrations}/catalog`,
  integrationsConnections = `${integrations}/connections`,
  integrationsConnection = `${integrationsConnections}/:connectionId`,
  integrationsConnectionsOauthAuthorize = `${integrationsConnections}/oauth/:provider/authorize`,
  integrationsAbcIgniteClubDiscovery = `${integrations}/providers/abcIgnite/clubs/:clubNumber/discovery`,
  integrationsAbcIgniteOnboarding = `${integrations}/providers/abcIgnite/onboarding`,
  igniteSyncConnectionClubs = '/api/ignite-sync/connections/:connectionId/clubs',
  additionalVoices = '/voice-templates',
  adminCheckout = '/stripe/admin-checkout',
  liveTransferScenarios = '/api/live-transfer-scenarios',
  liveTransferScenariosOrgLiveTransferType = `${liveTransferScenarios}/live-transfer-type/org`,
  liveTransferScenario = `${liveTransferScenarios}/:id`,
  liveTransferScenarioAgents = `${liveTransferScenario}/agents`,
  knowledgeSets = '/knowledge-sets',
  knowledgeSetsFromSources = `${knowledgeSets}/from-sources`,
  knowledgeSet = `${knowledgeSets}/:knowledgeSetId`,
  knowledgeSetFromSources = `${knowledgeSet}/from-sources`,
  knowledgeSetsDocumentPreSign = `${knowledgeSets}/documents/pre-sign`,
  knowledgeSetItems = `${knowledgeSet}/items`,
  knowledgeSetItemsFromSources = `${knowledgeSetItems}/from-sources`,
  knowledgeSetItemsBatch = `${knowledgeSetItems}/batch`,
  knowledgeSetItemsBatchDelete = `${knowledgeSetItemsBatch}/delete`,
  knowledgeSetItemsBatchSchedules = `${knowledgeSetItemsBatch}/schedules`,
  knowledgeSetClone = `${knowledgeSet}/clone`,
  knowledgeSetItem = `${knowledgeSetItems}/:knowledgeSetItemId`,
  knowledgeSetItemSchedules = `${knowledgeSetItem}/schedules`,
  knowledgeSetItemSchedule = `${knowledgeSetItemSchedules}/:scheduleId`,
  sendText = '/send-text',
  knowledgeSetSources = `${knowledgeSet}/sources`,
  knowledgeSetSource = `${knowledgeSetSources}/:sourceId`,
  knowledgeSetSourcesBatchDelete = `${knowledgeSetSources}/batch/delete`,
  // Knowledge v3: org-wide paginated lists, website pages, item locking
  knowledgeSetsSummary = `${knowledgeSets}/summary`,
  knowledgeSources = `${knowledgeSets}/sources`,
  knowledgeItems = `${knowledgeSets}/items`,
  knowledgeItemsSelection = `${knowledgeSets}/items/selection`,
  knowledgeSetsBatch = `${knowledgeSets}/batch`,
  knowledgeSetItemHistory = `${knowledgeSetItem}/history`,
  knowledgeActivityChanges = `${knowledgeSets}/activity/changes`,
  knowledgeActivityRuns = `${knowledgeSets}/activity/runs`,
  knowledgeSetsWebsiteDiscover = `${knowledgeSets}/website/discover`,
  knowledgeSetsWebsiteCheck = `${knowledgeSets}/website/check`,
  knowledgeSetWebsitePages = `${knowledgeSet}/website/pages`,
  knowledgeSetSourceWebsite = `${knowledgeSetSource}/website`,
  knowledgeSetSourceRescrape = `${knowledgeSetSource}/rescrape`,
  knowledgeSetSourceRescrapeScheduled = `${knowledgeSetSource}/rescrape/scheduled`,
  knowledgeSetSourceContent = `${knowledgeSetSource}/content`,
  knowledgeSetSourceCrawlContent = `${knowledgeSetSource}/crawls/:crawlId/content`,
  knowledgeSetSourceCrawlDiff = `${knowledgeSetSource}/crawls/:crawlId/diff`,
  knowledgeSetItemsBatchLock = `${knowledgeSetItemsBatch}/lock`,
  apiKeys = '/api/keys',
  apiKey = '/api/keys/:id',
  // Email service, through the shared private API (EHttpService.internal).
  unsubscribeConfirm = '/api/unsubscribe/confirm',
  // A recipient's unsubscribe link: open route, the token is the credential.
  unsubscribeRecipient = '/api/unsubscribe/recipient',
  emmaConnectionTest = '/api/email/test-emma-integration',
  brands = '/orgs/brands',
  brand = '/orgs/brand/:brandId',
  emailProviders = '/api/email/providers/:orgId',
  /**
   * Internal Api Gateway paths
   * Please add new internal api paths below
   */
  quote = '/guest/quotes/:quoteId',
  quoteCheckoutLink = '/guest/quotes/:quoteId/checkout-link',
  adminQuote = '/api/admin/quotes/:quoteId',
  adminQuotes = '/api/admin/quotes',
  adminOrganizations = '/api/admin/organizations',
  adminOrganization = `${adminOrganizations}/:organizationId`,
  adminOrganizationUsersCapabilities = `${adminOrganization}/users-capabilities`,
  adminAgents = '/api/admin/agents',
  adminPromptMetadata = '/api/admin/prompt-metadata',
  adminPromptMetadataItem = `${adminPromptMetadata}/:id`,
  apiUsersValidate = '/api/users/validate',
  apiUsersMePermissions = '/api/users/me/permissions',
  apiUsersPermissionsByCognitoId = '/api/users/:cognitoId/permissions',
  apiUsersEmailExists = '/api/users/email-exists',
  adminPlans = '/api/admin/plans',
  adminPlan = `${adminPlans}/:planId`,
  adminAddOns = '/api/admin/add-ons',
  adminAddOn = `${adminAddOns}/:addOnId`,
  adminTemplates = '/api/admin/templates',
  adminTemplate = `${adminTemplates}/:templateId`,
  adminVoiceTemplates = '/api/admin/voice-templates',
  adminVoiceTemplate = `${adminVoiceTemplates}/:voiceTemplateId`,
  adminVoiceTemplatesReorder = `${adminVoiceTemplates}/reorder`,
  adminUsageReportsOrgs = '/api/admin/usage-reports/orgs',
  adminUsageReportsLocations = '/api/admin/usage-reports/locations',
  adminUsageReportsOrgsExport = `${adminUsageReportsOrgs}/export`,
  adminUsageReportsLocationsExport = `${adminUsageReportsLocations}/export`,
  adminAdminUsers = '/api/admin/admin-users',
  adminAdminUserOrgs = `${adminAdminUsers}/orgs`,
  adminAdminUserRoles = `${adminAdminUsers}/roles`,
  adminAdminUserCandidates = `${adminAdminUsers}/candidates`,
  adminAdminUserAccess = `${adminAdminUsers}/:userId/access`,
  // Phone API
  phoneNumberStatus = '/api/phone-numbers/:phoneNumber',
  phoneNumbersMigrate = '/api/phone-numbers/migrate',
  phoneNumbersProvision = '/api/phone-numbers/provision',
  phoneNumbersDeprovision = '/api/phone-numbers/deprovision',
  agentsIntro = '/api/agents/intro',
  callsIntroBlendAudio = '/api/calls/recordIntro/blendaudio',
  callsOutbound = '/api/calls/outbound',
  phoneOrgPronunciationDictionary = '/api/phone/orgs/:orgId/pronunciation-dictionary',
  phoneOrgPronunciationDictionaryRules = `${phoneOrgPronunciationDictionary}/rules`,

  campaignTemplates = '/api/campaign-templates',
  campaignTemplate = `${campaignTemplates}/:templateId`,
  campaignTemplateVersions = `${campaignTemplate}/versions`,
  campaignTemplateVersion = `${campaignTemplate}/versions/:versionId`,
  campaignTemplateVersionPublish = `${campaignTemplateVersion}/publish`,
  campaignTemplateVersionDeactivate = `${campaignTemplateVersion}/deactivate`,
  campaignsTemplatesVerticalTags = `${campaignTemplates}/vertical-tags`,
  campaignCreateTemplateFromCampaign = `${campaignTemplates}/create-from-campaign`,
  agentExternalIds = '/agent-external-ids/:orgId',
  agentExternalIdsBulk = '/agent-external-ids/:orgId/bulk',
  agentExternalId = '/agent-external-ids/:orgId/:agentExternalId',
  locations = '/locations',
  locationsIntegrationMapping = `${locations}/integration-mapping`,
  locationsDelete = `${locations}/delete`,
  promptMetadata = '/prompt-metadata',
  analysisFields = '/analysis-fields',
  analysisFieldsAssistantRules = `${analysisFields}/assistant-rules`,
  analysisField = `${analysisFields}/:fieldId`,
  analysisFieldRules = `${analysisFields}/:fieldId/rules`,
  analysisFieldRule = `${analysisFieldRules}/:ruleId`,
  analysisFieldRuleAssignments = `${analysisFieldRule}/assignments`,
  analysisFieldRuleDuplicate = `${analysisFieldRule}/duplicate`,
  analysisFieldOrgDefault = `${analysisField}/org-default`,
  voipCalls = '/api/voip/calls',
  voipCallRead = `${voipCalls}/:callId/read`,
  voipLocations = '/api/voip/locations',
  voipDevices = '/api/voip/devices',
  voipNumbers = '/api/voip/numbers',
  voipNumberById = `${voipNumbers}/:numberId`,
  voipNumberAssignments = `${voipNumbers}/:numberId/assignments`,
  voipNumberUnassignDevice = `${voipNumbers}/:numberId/unassign-device`,
  voipNumberAssignDevice = `${voipNumbers}/:numberId/assign-device`,
  voipNumbersPort = `${voipNumbers}/port`,
  voipNumbersSearch = `${voipNumbers}/search`,
  voipNumbersBatchPurchase = `${voipNumbers}/batch-purchase`,
  voipVoicemails = '/api/voip/voicemails',
  voipVoicemailPlaybackUrl = `${voipVoicemails}/:callId/playback-url`,
  reportingCalls = '/api/reporting/calls',
  reportingFilterOptions = '/api/reporting/filter-options',
  reportingVoipMatchback = '/api/reporting/voip-matchback',
  reportingOrgMetricsExport = '/api/reporting/org-metrics-export',
  reportingExportJob = '/api/reporting/exports/:jobId',
  reportingExecutiveSummaryPdf = '/api/reporting/executive-summary/pdf',
  reportingLiveTransferDestinations = '/api/reporting/assistants/:agentId/live-transfer-destinations',
  reportingLinksSent = '/api/reporting/assistants/:agentId/links-sent',
  reportingContactsForReports = '/api/reporting/contacts/reports',
  reportingConversationTotals = '/api/reporting/conversation-totals',
  reportingAssistantPerformance = '/api/reporting/assistant-performance',
  crmIntegrations = '/api/crm/integrations',
  crmIntegration = '/api/crm/integrations/:id',
}

export enum EHttpErrorName {
  userNotFound = 'USER_NOT_FOUND',
  conversationNotFound = 'CONVERSATION_NOT_FOUND',
  agentNotFound = 'AGENT_NOT_FOUND',
  contactNotFound = 'CONTACT_NOT_FOUND',
}

export type TErrorResponse = {
  response?: {
    data?: {
      error?: Record<string, string[]>
      message?: string
    }
  }
}
